/**
 * Timezone-safe date helpers.
 *
 * HTML date inputs return "YYYY-MM-DD" strings. Converting them to JS Date
 * objects with `new Date("YYYY-MM-DD")` interprets the value as UTC midnight,
 * but local-timezone methods (getMonth, getDate, etc.) can shift the date by
 * ±1 day depending on the user's timezone offset. These helpers avoid that
 * by working directly with the date string parts.
 */

/**
 * Parse a "YYYY-MM-DD" string into its numeric parts without creating a Date object.
 * Returns null if the string is not a valid date format.
 */
export function parseDateParts(value: string): { year: number; month: number; day: number } | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    // Validate the date is real (e.g., reject Feb 30) using local Date constructor
    // with explicit parts — this avoids the UTC-parsing timezone trap.
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }

    return { year, month, day };
}

/**
 * Check if a "YYYY-MM-DD" string represents a valid calendar date.
 */
export function isValidDateString(value: string): boolean {
    return parseDateParts(value) !== null;
}

/**
 * Check if a "YYYY-MM-DD" string falls within the given range.
 * Both min and max use { year, month, day } to avoid timezone issues.
 */
export function isDateInRange(
    value: string,
    min: { year: number; month: number; day: number },
    max?: { year: number; month: number; day: number },
): boolean {
    const parts = parseDateParts(value);
    if (!parts) return false;

    const date = new Date(parts.year, parts.month - 1, parts.day);
    const minDate = new Date(min.year, min.month - 1, min.day);

    if (date < minDate) return false;

    if (max) {
        const maxDate = new Date(max.year, max.month - 1, max.day);
        if (date > maxDate) return false;
    }

    return true;
}

/**
 * Convert a date value (string, Date, or numeric Unix timestamp in ms) to a
 * "YYYY-MM-DD" string suitable for an HTML date input, without timezone shifts.
 *
 * - Strings: sliced to the first 10 chars ("YYYY-MM-DD").
 * - Date objects: UTC year/month/day extracted to avoid local-offset shifts.
 * - Numbers: treated as a Unix timestamp in milliseconds and converted via UTC methods.
 */
export function toDateInputValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'string') return value.slice(0, 10);

    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return '';
        const date = new Date(value);
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const d = String(date.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    if (value instanceof Date) {
        const y = value.getUTCFullYear();
        const m = String(value.getUTCMonth() + 1).padStart(2, '0');
        const d = String(value.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    return '';
}

/**
 * Format a date value for display as "MM/DD/YYYY" without timezone shifts.
 */
export function formatDateDisplay(value: unknown): string {
    if (!value) return '';

    const dateStr = toDateInputValue(value);
    const parts = parseDateParts(dateStr);
    if (!parts) return '';

    const mm = String(parts.month).padStart(2, '0');
    const dd = String(parts.day).padStart(2, '0');
    return `${mm}/${dd}/${parts.year}`;
}
